import { PubnubService } from 'go-modules/go-pubnub/pubnub.service';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { FullstoryService } from 'go-modules/services/fullstory/fullstory.service';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { ACTIVITY_SOURCES, ActivitySourceDialogComponent } from '@go-modules/components/dialogs/activity-source-dialog/activity-source-dialog.component';
import { SelectedService } from 'go-modules/services/selected/selected.service';
import { ActivityEditorAiMarkersTour } from 'go-modules/activity-editor/activity-editor-ai-markers-tour/activity-editor-ai-markers-tour.service';
import { ActivityEditorDefaultMarkerSetTour } from 'go-modules/activity-editor/activity-editor-default-marker-set-tour/activity-editor-default-marker-set-tour.service';
import { CopyPreviousAssignmentDialogComponent } from '@go-modules/components/dialogs/copy-previous-assignment-dialog/copy-previous-assignment-dialog.component';
import { ProductTier } from '@go-modules/interfaces/licenses/product-tier';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { LibraryDialogComponent, LibraryDialogData } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import type { Activity } from '@go-modules/interfaces/activity';
import {
	ActivityPreviewEditorDialogService,
	type ActivityPreviewEditorDialogData,
	type ActivityPreviewEditorDialogCloseResult
} from '@go-modules/components/dialogs/activity-preview-editor/activity-preview-editor-dialog.service';

/* @ngInject */
export function ActivityEditorWizardViewController (
	$scope,
	$log,
	ActivityModel,
	ActivityTemplateModel,
	Group,
	MediaModel,
	pubnub: PubnubService,
	appData,
	ngxGoModalService: GoModalService,
	fullstoryService: FullstoryService,
	eventService: EventService,
	selectedService: SelectedService,
	activityEditorAiMarkersTour: ActivityEditorAiMarkersTour,
	activityEditorDefaultMarkerSetTour: ActivityEditorDefaultMarkerSetTour,
	featureFlag: NgxFeatureFlagService,
	ngxActivityPreviewEditorDialogService: ActivityPreviewEditorDialogService
) {
	const vm = this;
	const environmentVarsService = EnvironmentVarsService.getInstance();
	const shouldUseActivityPreviewEditorDialog = () =>
		featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG') &&
		vm.activity?.activity_id != null;
	const shouldShowWizardBackButton = () => {
		const prevView = vm.wizardController?.wizard?.getPrevView?.();
		return !!prevView && !prevView.hideNextPrevButton;
	};

	/**
	 * Handles controller init
	 */
	vm.$onInit = () => {
		// Fetch activity templates
		vm.activityTemplates = [];
		vm.showActivityPanel = false;
		vm.group = appData.userGroup;
		vm.activity = appData.activity;
		vm.user = appData.currentUser;
		vm.hideDates = appData.hideDates;

		// Activity editor header options
		vm.headerOptions = {
			hideName: true
		};

		if (!vm.group) {
			throw new Error('Param `group` is required by ActivityEditorContainerController');
		} else if (!(vm.activity instanceof ActivityModel)) {
			throw new Error('Param `activity` is required by ActivityEditorContainerController');
		}

		// Fetch org settings
		Group.getOrgSettings({group_id: vm.group.org_id}).then((settings) => {
			vm.orgSettings = settings;
		});

		// Subscribe to pubnub channels
		const pubnubChannel = environmentVarsService.get(EnvironmentVarsService.VAR.USER_PUBNUB_CHANNEL);
		if (!pubnubChannel || pubnubChannel === undefined) {
			vm.channels = [];
		} else {
			vm.channels = [environmentVarsService.get(EnvironmentVarsService.VAR.USER_PUBNUB_CHANNEL)];
		}
		vm.channels.unshift(vm.group.channel);
		pubnub.sub(vm.channels, 'wizard-view', vm.messageHandler);

		if (!vm.activity.template?.id) {
			ActivityTemplateModel.query().$promise.then((templates) => {
				vm.activityTemplates = templates;
				let template = templates.find((t) => t.slug === ActivityTemplateModel.TYPE.STANDARD);
				if (vm.activity.activity_template_id) {
					template = ActivityTemplateModel.model({
						id: vm.activity.activity_template_id
					});
				}
				vm.activity.setActivityTemplate(template);

				vm.showActivity();
			});
		} else {
			vm.activityTemplates = ActivityTemplateModel.query();
		}

		ngxGoModalService.open(ActivitySourceDialogComponent, true)
			.afterClosed().subscribe((source: ACTIVITY_SOURCES | undefined) => {
				if (source === ACTIVITY_SOURCES.LIBRARY || source === ACTIVITY_SOURCES.CREATE) {
					const data: LibraryDialogData = {
						filterType: [CONTENT_TYPES.ACTIVITIES],
						mode: MODES.SELECT,
						collectionManagement: true
					};
					if (source === ACTIVITY_SOURCES.CREATE) {
						fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_ASSIGNMENT_SOURCE_TEMPLATE, {});
						data.create = true;
						data.openToOrg = selectedService.getOrg().group_id;
					} else {
						fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_ASSIGNMENT_SOURCE_LIBRARY, {});
					}

					ngxGoModalService.open(LibraryDialogComponent, false, {
						data
					}).afterClosed().subscribe((item: any) => {
						if (item) {
							const oldActivityName = vm.activity.name;
							// need to remove activity panel and then make a new one
							vm.showActivityPanel = false;
							vm.activity.$copy({
								sourceActivity: item.id
							}).then(() => {
								vm.activity.name = oldActivityName;
								vm.showActivityPanel = false;
								this.completeSetup();
							});
						} else {
							vm.showActivity();
						}
					});
				} else if (source === ACTIVITY_SOURCES.PREVIOUS) {
					fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_ASSIGNMENT_SOURCE_PREVIOUS, {});
					ngxGoModalService.open(CopyPreviousAssignmentDialogComponent, false, {
						data: {currentActivity: vm.activity}
					}).afterClosed().subscribe((assignment: any) => {
						if (assignment) {
							const oldActivityName = vm.activity.name;
							// need to remove activity panel and then make a new one
							vm.showActivityPanel = false;
							vm.activity.$copy({
								sourceActivity: assignment.activity_id
							}).then(() => {
								vm.activity.name = oldActivityName;

								if (assignment.parent_child_sync && assignment.is_parent_sync_enabled) {
									this.completeSetup();
								} else {
									vm.showActivity();
								}
							});
						} else {
							vm.showActivity();
						}
						vm.showActivityEditorDefaultMarkerSetTour();
						vm.showActivityEditorAiMarkersTour();
					});
				} else if (source === ACTIVITY_SOURCES.NEW) {
					fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_ASSIGNMENT_SOURCE_NEW, {});

					vm.showActivity();
					vm.showActivityEditorDefaultMarkerSetTour();
					vm.showActivityEditorAiMarkersTour();
				} else {
					vm.showActivity();
					vm.showActivityEditorDefaultMarkerSetTour();
					vm.showActivityEditorAiMarkersTour();
				}
			});
	};

	vm.showActivity = () => {
		const license = selectedService.getLicense();
		if (!featureFlag.isAvailable('AI_DEFAULT') || !license || license.salesforce_license.tier_name !== ProductTier.Plus) {
			vm.activity.llm_definitions = [];
		}

		if (shouldUseActivityPreviewEditorDialog()) {
			vm.openActivityPreviewEditorDialog();
			return;
		}

		vm.showLegacyActivityEditor();
	};

	vm.showLegacyActivityEditor = () => {
		vm.showActivityPanel = true;
		// safeDigest
		const phase = $scope.$root.$$phase || $scope.$$phase;
		if (!phase) {
			$scope.$digest();
		}
	};

	vm.openActivityPreviewEditorDialog = () => {
		const dialogData: ActivityPreviewEditorDialogData = {
			mode: 'edit',
			activityId: vm.activity.activity_id,
			activityDraft: vm.activity,
			group: vm.group,
			org: selectedService.getOrg(),
			license: selectedService.getLicense(),
			environment: environmentVarsService.get(EnvironmentVarsService.VAR.ENVIRONMENT),
			isSaveRequired: true,
			syncSelectedActivity: {
				activityId: vm.activity.activity_id,
				sync: (activity: Activity) => {
					if (selectedService.getActivity()?.activity_id !== activity.activity_id) {
						return;
					}

					selectedService.setActivity(ActivityModel.model(activity));
				}
			}
		};
		if (shouldShowWizardBackButton()) {
			Object.assign(dialogData, {
				onNavigateBack: () => vm.wizardController.prev()
			});
		}

		return ngxActivityPreviewEditorDialogService.open(dialogData).then((dialogRef) => {
			dialogRef.afterClosed().subscribe((result: ActivityPreviewEditorDialogCloseResult) => {
				if (result == null || 'dismissed' in result) {
					return;
				}

				Object.assign(vm.activity, result.activity);
				vm.completeSetup();
			});
		})
			.catch(() => vm.showLegacyActivityEditor());
	};

	vm.showActivityEditorAiMarkersTour = () => {
		if (!vm.activity.isConversationBoard() && selectedService.getLicense()?.salesforce_license.ai_prompts_enabled) {
			activityEditorAiMarkersTour.getTour().start();
		}
	};

	vm.showActivityEditorDefaultMarkerSetTour = () => {
		if (vm.activity.tag_set_id !== null) {
			activityEditorDefaultMarkerSetTour.getTour().start();
		}
	};

	/**
	 * Handles controller destroy
	 */
	vm.$onDestroy = () => {
		// Unsubscribe from pubnub group channel
		pubnub.unsub(vm.channels, 'wizard-view');
	};

	/**
	 * All incoming messages are received here
	 *
	 * @param message
	 */
	vm.messageHandler = (message) => {
		$scope.$apply(() => {
			Object.keys(message).forEach((property) => {
				const item: any = message[property];
				switch (property) {
					case 'media':
						const media = MediaModel.model(item);
						// Broadcast media sync event
						eventService.broadcast(EVENT_NAMES.MEDIA_SYNC, media);
						break;
				}
			});
		});
	};

	vm.completeSetup = () => {
		vm.activity.setupComplete()
			.$promise
			.then(() => this.wizardController.finish())
			.catch((response) => $log.error('Failed to setup activity!', response));
	};
}
